import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Shared";

// Contact form is decoupled from FastAPI/MongoDB. It posts to a simple PHP
// endpoint that lives on the same static host (dhosting). Override with
// REACT_APP_CONTACT_ENDPOINT if the file is placed elsewhere.
const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || "/kontakt.php";

const inputCls =
    "w-full border border-stone bg-white px-4 py-3 text-base text-pine placeholder:text-muted-foreground focus:border-pine focus:outline-none transition-colors duration-300";

// Official BedBooking widget embed. BedBooking is the single source of
// availability, prices, cottage selection and the booking process.
const BB_WIDGET_ID = "53389a03a4bfb487845c2da4edacb04ba9829440";

function BedBookingWidget() {
    const inited = useRef(false);
    useEffect(() => {
        if (inited.current) return;
        inited.current = true;
        const o = "w1";
        (function (w, d, s, o, f) {
            if (w[o]) return;
            w["BB-Widget"] = o;
            w[o] = w[o] || function () { (w[o].q = w[o].q || []).push(arguments); };
            const js = d.createElement(s);
            const fjs = d.getElementsByTagName(s)[0];
            js.id = o; js.src = f; js.async = 1;
            fjs.parentNode.insertBefore(js, fjs);
        })(window, document, "script", o, "https://bed-booking.com/widget/widget.js");
        window.w1("init", { targetElementId: "widgetHolder_enwdop5u6", widgetId: BB_WIDGET_ID, type: "calendar" });
    }, []);
    return <div id="widgetHolder_enwdop5u6" className="w-full" data-testid="bedbooking-widget" />;
}

export default function Reservation() {
    const [sending, setSending] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 2, message: "" });

    const submitContact = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const r = await axios.post(CONTACT_ENDPOINT, {
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                guests: Number(form.guests),
                message: form.message || null,
            });
            toast.success(r.data?.message || "Dziękujemy! Wiadomość została wysłana.");
            setForm({ name: "", email: "", phone: "", guests: 2, message: "" });
        } catch (err) {
            toast.error(err.response?.data?.detail || err.response?.data?.message || "Nie udało się wysłać wiadomości. Spróbuj ponownie.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="rezerwacja" data-testid="reservation-section" className="grid lg:grid-cols-[2fr_1fr]">
            <div className="bg-white px-6 py-16 md:px-12 lg:py-24 xl:px-20">
                <Reveal>
                    <p className="overline-label" data-testid="reservation-eyebrow">Rezerwacja</p>
                    <h2 className="mt-5 font-serif text-[clamp(2.1rem,1.2rem+2.4vw,3.9rem)] leading-[1.08] tracking-[-1.2px] text-pine" data-testid="reservation-title">
                        Zarezerwuj on-line
                    </h2>
                    <p className="mt-5 max-w-md text-[17px] leading-[1.6] text-sage">
                        Wybierz termin, sprawdź dostępne domki, ceny i szczegóły, a następnie zarezerwuj pobyt bezpośrednio w naszym systemie rezerwacji.
                    </p>
                    <div className="mt-8">
                        <BedBookingWidget />
                    </div>
                </Reveal>
            </div>

            <div id="kontakt-form" className="bg-[#EEF4EB] px-6 py-16 md:px-12 lg:py-24 xl:px-20" data-testid="contact-panel">
                <Reveal delay={0.1}>
                    <p className="overline-label" data-testid="contact-eyebrow">Kontakt</p>
                    <h2 className="mt-5 font-serif text-[clamp(2.1rem,1.2rem+2.4vw,3.9rem)] leading-[1.08] tracking-[-1.2px] text-pine" data-testid="contact-title">
                        Zarezerwuj i zapłać na miejscu.
                    </h2>
                    <p className="mt-5 max-w-md text-[17px] leading-[1.6] text-sage">
                        Zostaw nam wiadomość, a potwierdzimy dostępność. Rezerwację możesz opłacić gotówką na miejscu.
                    </p>
                    <form onSubmit={submitContact} className="mt-9 flex flex-col gap-5" data-testid="contact-form">
                        <div>
                            <label htmlFor="contact-name" className="mb-2 block text-[13px] font-semibold uppercase tracking-[3px] text-sage">Imię i nazwisko *</label>
                            <input
                                id="contact-name"
                                data-testid="contact-name-input"
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className={inputCls}
                                placeholder="Jan Kowalski"
                            />
                        </div>
                        <div>
                            <label htmlFor="contact-email" className="mb-2 block text-[13px] font-semibold uppercase tracking-[3px] text-sage">E-mail *</label>
                            <input
                                id="contact-email"
                                data-testid="contact-email-input"
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className={inputCls}
                                placeholder="jan@przyklad.pl"
                            />
                        </div>
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="contact-phone" className="mb-2 block text-[13px] font-semibold uppercase tracking-[3px] text-sage">Telefon</label>
                                <input
                                    id="contact-phone"
                                    data-testid="contact-phone-input"
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    className={inputCls}
                                    placeholder="+48 ..."
                                />
                            </div>
                            <div>
                                <label htmlFor="contact-guests" className="mb-2 block text-[13px] font-semibold uppercase tracking-[3px] text-sage">Goście</label>
                                <select
                                    id="contact-guests"
                                    data-testid="contact-guests-select"
                                    value={form.guests}
                                    onChange={(e) => setForm({ ...form, guests: e.target.value })}
                                    className={inputCls}
                                >
                                    {[1, 2, 3, 4].map((n) => (
                                        <option key={n} value={n}>{n} {n === 1 ? "osoba" : "osoby"}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label htmlFor="contact-message" className="mb-2 block text-[13px] font-semibold uppercase tracking-[3px] text-sage">Wiadomość</label>
                            <textarea
                                id="contact-message"
                                data-testid="contact-message-input"
                                rows={4}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                className={`${inputCls} resize-none`}
                                placeholder=""
                            />
                        </div>
                        <button
                            data-testid="contact-submit-btn"
                            type="submit"
                            disabled={sending}
                            className="btn-primary w-full disabled:opacity-60"
                        >
                            {sending ? "Wysyłanie..." : "Wyślij wiadomość"}
                        </button>
                        <p className="text-center text-sm text-muted-foreground">
                            Odpowiadamy zwykle w ciągu 24 godzin.
                        </p>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
